import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Container, 
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../assets/company_logo.png';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/projects', label: 'Projects' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/founders', label: 'Founders' },
    { path: '/contact', label: 'Contact' }
  ];

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const mobileMenu = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleDrawerToggle}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '300px',
          background: theme => `
            linear-gradient(
              135deg,
              ${theme.palette.primary.main} 0%,
              ${theme.palette.primary.dark} 100%
            )
          `,
          color: 'white'
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          sx={{ '&:hover': { transform: 'rotate(90deg)', transition: 'transform 0.3s' } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ pt: 0 }}>
        {navItems.map((item) => (
          <ListItem 
            key={item.path}
            onClick={handleDrawerToggle}
            component={Link}
            to={item.path}
            sx={{
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              '&:last-child': { borderBottom: 'none' },
              backgroundColor: isActive(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' }
            }}
          >
            <ListItemText 
              primary={item.label} 
              sx={{ 
                color: 'white',
                '& .MuiTypography-root': {
                  fontWeight: isActive(item.path) ? 'bold' : 'normal'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return (
    <AppBar 
      position="static"
      sx={{
        background: theme => `
          linear-gradient(
            135deg,
            ${theme.palette.primary.main} 0%,
            ${theme.palette.primary.dark} 100%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '5%',
          left: 0,
          right: 0,
          height: '60%',
          background: `
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 300'%3E%3Cg fill='rgba(255,255,255,0.15)'%3E%3C!-- Left side buildings --%3E%3Cpath d='M0,300 L60,300 L60,150 L30,100 L0,150 Z'/%3E%3Cpath d='M70,300 L120,300 L120,80 L95,50 L70,80 Z'/%3E%3Cpath d='M130,300 L180,300 L180,120 L155,90 L130,120 Z'/%3E%3C!-- Central buildings with Ferris wheel --%3E%3Cpath d='M200,300 L260,300 L260,60 L230,30 L200,60 Z'/%3E%3Cpath d='M280,300 L330,300 L330,100 L305,70 L280,100 Z'/%3E%3C!-- Ferris wheel --%3E%3Ccircle cx='400' cy='150' r='50' fill='none' stroke='rgba(255,255,255,0.15)' stroke-width='2'/%3E%3Ccircle cx='400' cy='150' r='45' fill='none' stroke='rgba(255,255,255,0.15)' stroke-width='2'/%3E%3C!-- Spokes --%3E%3Cpath d='M400,100 L400,200 M350,150 L450,150 M365,115 L435,185 M365,185 L435,115' stroke='rgba(255,255,255,0.15)' stroke-width='2' fill='none'/%3E%3C!-- Right side buildings --%3E%3Cpath d='M470,300 L520,300 L520,90 L495,60 L470,90 Z'/%3E%3Cpath d='M530,300 L590,300 L590,70 L560,40 L530,70 Z'/%3E%3Cpath d='M600,300 L650,300 L650,110 L625,80 L600,110 Z'/%3E%3C!-- Pattern repeats --%3E%3Cpath d='M670,300 L730,300 L730,150 L700,100 L670,150 Z'/%3E%3Cpath d='M740,300 L790,300 L790,80 L765,50 L740,80 Z'/%3E%3Cpath d='M800,300 L850,300 L850,120 L825,90 L800,120 Z'/%3E%3Cpath d='M860,300 L920,300 L920,60 L890,30 L860,60 Z'/%3E%3C/g%3E%3C/svg%3E")
          `,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat-x',
          animation: 'moveBuildings 180s linear infinite',
          opacity: 1,
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '5%',
          left: 0,
          right: 0,
          height: '60%',
          background: `
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 300'%3E%3Cg fill='rgba(255,255,255,0.05)'%3E%3C!-- Left side buildings --%3E%3Cpath d='M0,0 L60,0 L60,150 L30,200 L0,150 Z'/%3E%3Cpath d='M70,0 L120,0 L120,220 L95,250 L70,220 Z'/%3E%3Cpath d='M130,0 L180,0 L180,180 L155,210 L130,180 Z'/%3E%3C!-- Central buildings with Ferris wheel --%3E%3Cpath d='M200,0 L260,0 L260,240 L230,270 L200,240 Z'/%3E%3Cpath d='M280,0 L330,0 L330,200 L305,230 L280,200 Z'/%3E%3C!-- Ferris wheel --%3E%3Ccircle cx='400' cy='150' r='50' fill='none' stroke='rgba(255,255,255,0.05)' stroke-width='2'/%3E%3Ccircle cx='400' cy='150' r='45' fill='none' stroke='rgba(255,255,255,0.05)' stroke-width='2'/%3E%3C!-- Spokes --%3E%3Cpath d='M400,100 L400,200 M350,150 L450,150 M365,115 L435,185 M365,185 L435,115' stroke='rgba(255,255,255,0.05)' stroke-width='2' fill='none'/%3E%3C!-- Right side buildings --%3E%3Cpath d='M470,0 L520,0 L520,210 L495,240 L470,210 Z'/%3E%3Cpath d='M530,0 L590,0 L590,230 L560,260 L530,230 Z'/%3E%3Cpath d='M600,0 L650,0 L650,190 L625,220 L600,190 Z'/%3E%3C!-- Pattern repeats --%3E%3Cpath d='M670,0 L730,0 L730,150 L700,200 L670,150 Z'/%3E%3Cpath d='M740,0 L790,0 L790,220 L765,250 L740,220 Z'/%3E%3Cpath d='M800,0 L850,0 L850,180 L825,210 L800,180 Z'/%3E%3Cpath d='M860,0 L920,0 L920,240 L890,270 L860,240 Z'/%3E%3C/g%3E%3C/svg%3E")
          `,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat-x',
          transform: 'scaleY(-1)',
          animation: 'moveBuildings 180s linear infinite',
          opacity: 0.3,
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 } }}>
        <Toolbar sx={{ justifyContent: 'space-between', padding: '8px 0', position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
            <img 
              src={logo} 
              alt="4 Roots Realty" 
              style={{ 
                height: isMobile ? '32px' : '40px', 
                width: isMobile ? '32px' : '40px' 
              }} 
            />
            <Typography 
              variant="h6" 
              component={Link} 
              to="/" 
              sx={{ 
                textDecoration: 'none', 
                color: 'white',
                fontWeight: 'bold',
                fontSize: isMobile ? '1.1rem' : '1.25rem',
                '&:hover': { opacity: 0.9 }
              }}
            >
              4 Roots Realty
            </Typography>
          </Box>
          
          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ ml: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, zIndex: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  component={Link}
                  to={item.path}
                  sx={{
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: isActive(item.path) ? 'translateX(-50%) scaleX(1)' : 'translateX(-50%) scaleX(0)',
                      transformOrigin: 'center',
                      width: '80%',
                      height: '2px',
                      backgroundColor: 'white',
                      transition: 'transform 0.3s ease-in-out'
                    },
                    '&:hover::after': {
                      transform: 'translateX(-50%) scaleX(1)'
                    },
                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                    opacity: isActive(item.path) ? 1 : 0.85
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
        </Toolbar>
      </Container>
      {mobileMenu}
    </AppBar>
  );
};

export default Navbar; 